import { Button } from "@/components/button";
import { Combobox } from "@/components/combobox";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { ExperimentType, type ExperimentImportMetadata, type ExperimentVariant } from "@/http/api-types";
import { PREDICTOR_NAMES, predictorTypeName } from "@/lib/predictor-types";
import { TrashIcon } from "@radix-ui/react-icons";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ExperimentFormValues } from "./ExperimentForm";
import { EXPERIMENT_FORM_LABEL_CLASS } from "./experiment-form-styles";
import type { ExperimentFormModel } from "./use-experiment-form-model";
import type { ExperimentVariantParamHandlers } from "./use-experiment-variant-params";

// Sentinel for the "clear this cell" option, since the Combobox/cmdk does not handle empty option values.
const NONE_CELL_VALUE = "__none__";

const formatTrafficAllocationDisplay = (value: number): string => {
  return (value / 100).toFixed(2);
};

const parseTrafficAllocationInput = (input: string): number => {
  const parsed = parseFloat(input);
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
};

const getJsonTextareaRows = (value: string) => {
  const lineCount = value.split("\n").length;
  return Math.min(Math.max(lineCount, 4), 10);
};

type ExperimentVariantsProps = {
  experimentType: ExperimentType;
  audiences: string[];
  audienceOptionsLoading: boolean;
  predictionFlightsLoading: boolean;
  campaignFields: Array<{ id: string }>;
  campaigns: ExperimentFormValues["campaigns"];
  campaignOptions: Array<{ id: string | number; name: string }>;
  appendCampaign: ExperimentFormModel["appendCampaign"];
  removeCampaign: ExperimentFormModel["removeCampaign"];
  addCampaignParam: ExperimentVariantParamHandlers["addCampaignParam"];
  removeCampaignParam: ExperimentVariantParamHandlers["removeCampaignParam"];
  updateCampaignParam: ExperimentVariantParamHandlers["updateCampaignParam"];
  totalCampaignTrafficAllocation: number;
  repoBranchPairs: ExperimentFormValues["repoBranchPairs"];
  repoBranchPairFields: Array<{ id: string }>;
  appendRepoBranchPair: ExperimentFormModel["appendRepoBranchPair"];
  removeRepoBranchPair: ExperimentFormModel["removeRepoBranchPair"];
  monitoredModels: string[];
  predictionFlightFields: Array<{ id: string }>;
  predictionFlights: ExperimentFormValues["predictionFlights"];
  predictionFlightOptions: Array<{ label: string; value: string }>;
  importMetadata: ExperimentImportMetadata | null;
  appendPredictionFlight: ExperimentFormModel["appendPredictionFlight"];
  removePredictionFlight: ExperimentFormModel["removePredictionFlight"];
  addPredictionFlightParam: ExperimentVariantParamHandlers["addPredictionFlightParam"];
  removePredictionFlightParam: ExperimentVariantParamHandlers["removePredictionFlightParam"];
  updatePredictionFlightParamValue: ExperimentVariantParamHandlers["updatePredictionFlightParamValue"];
  updatePredictionFlightParamType: ExperimentVariantParamHandlers["updatePredictionFlightParamType"];
  lockPredictionFlights: boolean;
  fieldErrors: Record<string, string>;
};

export function ExperimentVariants({
  experimentType,
  audiences,
  audienceOptionsLoading,
  predictionFlightsLoading,
  campaignFields,
  campaigns,
  campaignOptions,
  appendCampaign,
  removeCampaign,
  addCampaignParam,
  removeCampaignParam,
  updateCampaignParam,
  totalCampaignTrafficAllocation,
  repoBranchPairs,
  repoBranchPairFields,
  appendRepoBranchPair,
  removeRepoBranchPair,
  monitoredModels,
  predictionFlightFields,
  predictionFlights,
  predictionFlightOptions,
  importMetadata,
  appendPredictionFlight,
  removePredictionFlight,
  addPredictionFlightParam,
  removePredictionFlightParam,
  updatePredictionFlightParamValue,
  updatePredictionFlightParamType,
  lockPredictionFlights,
  fieldErrors,
}: ExperimentVariantsProps) {
  const { setValue, register } = useFormContext<ExperimentFormValues>();
  const campaignNameById = useMemo(
    () => new Map(campaignOptions.map(campaign => [campaign.id.toString(), campaign.name])),
    [campaignOptions],
  );
  const testCampaignIndexes = useMemo(
    () =>
      campaigns
        .map((campaign, index) => ({ campaign, index }))
        .filter(({ campaign }) => campaign.campaignId && campaign.variant === "test"),
    [campaigns],
  );

  // Test-group matrix: rows = control campaigns, columns = test groups, cells = test campaigns.
  const controlRows = useMemo(
    () =>
      campaigns
        .map((campaign, index) => ({ campaign, index }))
        .filter(({ campaign }) => campaign.campaignId && campaign.variant === "control"),
    [campaigns],
  );
  const maxAssignedGroup = useMemo(
    () =>
      campaigns.reduce(
        (max, campaign) =>
          campaign.variant === "test" && campaign.campaignId && campaign.testGroup
            ? Math.max(max, campaign.testGroup)
            : max,
        0,
      ),
    [campaigns],
  );
  // The test campaign placed in each (control, group) cell, keyed "controlId:group".
  const placedTestByCell = useMemo(() => {
    const map = new Map<string, { campaignId: string; index: number }>();
    campaigns.forEach((campaign, index) => {
      if (campaign.variant === "test" && campaign.campaignId && campaign.controlCampaignId && campaign.testGroup) {
        map.set(`${campaign.controlCampaignId}:${campaign.testGroup}`, { campaignId: campaign.campaignId, index });
      }
    });
    return map;
  }, [campaigns]);
  // Test campaigns not yet placed in any cell — eligible for any empty cell.
  const unassignedTests = useMemo(
    () =>
      campaigns
        .map((campaign, index) => ({ campaign, index }))
        .filter(
          ({ campaign }) =>
            campaign.campaignId && campaign.variant === "test" && !(campaign.controlCampaignId && campaign.testGroup),
        ),
    [campaigns],
  );

  // With N controls each holding one test per group, M test campaigns need at least ceil(M / N) groups —
  // pre-show that many columns so users don't have to add them manually.
  const requiredGroupColumns = controlRows.length > 0 ? Math.ceil(testCampaignIndexes.length / controlRows.length) : 1;
  const [extraGroupColumns, setExtraGroupColumns] = useState(0);
  const groupCount = Math.max(1, maxAssignedGroup, requiredGroupColumns) + extraGroupColumns;

  const handleCellChange = (controlId: string, group: number, testCampaignId: string) => {
    // Vacate whoever currently sits in this cell (when replacing or clearing).
    const current = placedTestByCell.get(`${controlId}:${group}`);
    if (current && current.campaignId !== testCampaignId) {
      setValue(`campaigns.${current.index}.controlCampaignId`, "", { shouldDirty: true });
      setValue(`campaigns.${current.index}.testGroup`, undefined, { shouldDirty: true });
    }
    if (testCampaignId) {
      const index = campaigns.findIndex(campaign => campaign.campaignId === testCampaignId);
      if (index >= 0) {
        setValue(`campaigns.${index}.controlCampaignId`, controlId, { shouldDirty: true });
        setValue(`campaigns.${index}.testGroup`, group, { shouldDirty: true });
      }
    }
  };

  const campaignLabel = (campaignID: string) => {
    const name = campaignNameById.get(campaignID);
    return name ? `${campaignID} - ${name}` : `Campaign ${campaignID}`;
  };

  const handleCampaignIDChange = (index: number, value: string) => {
    const previousCampaignID = campaigns[index]?.campaignId || "";
    const currentVariant = campaigns[index]?.variant;
    setValue(`campaigns.${index}.campaignId`, value, { shouldDirty: true });

    if (currentVariant === "test" && campaigns[index]?.controlCampaignId === value) {
      setValue(`campaigns.${index}.controlCampaignId`, "", { shouldDirty: true });
      return;
    }

    if (currentVariant !== "control") return;

    if (!previousCampaignID) {
      return;
    }

    campaigns.forEach((campaign, campaignIndex) => {
      if (campaign.controlCampaignId !== previousCampaignID) return;
      setValue(`campaigns.${campaignIndex}.controlCampaignId`, value, { shouldDirty: true });
    });
  };

  const handleCampaignVariantChange = (index: number, value: ExperimentVariant) => {
    const campaignID = campaigns[index]?.campaignId || "";
    const wasControl = campaigns[index]?.variant === "control";
    setValue(`campaigns.${index}.variant`, value, { shouldDirty: true });

    if (value === "control") {
      setValue(`campaigns.${index}.controlCampaignId`, "", { shouldDirty: true });
      setValue(`campaigns.${index}.testGroup`, undefined, { shouldDirty: true });
      return;
    }

    if (wasControl && campaignID) {
      campaigns.forEach((campaign, campaignIndex) => {
        if (campaign.controlCampaignId !== campaignID) return;
        setValue(`campaigns.${campaignIndex}.controlCampaignId`, "", { shouldDirty: true });
      });
    }

    if (campaigns[index]?.controlCampaignId === campaignID) {
      setValue(`campaigns.${index}.controlCampaignId`, "", { shouldDirty: true });
    }

    if (!campaigns[index]?.testGroup) {
      setValue(`campaigns.${index}.testGroup`, 1, { shouldDirty: true });
    }
  };

  const handleRemoveCampaign = (index: number) => {
    const campaignID = campaigns[index]?.campaignId || "";
    const isControl = campaigns[index]?.variant === "control";

    if (campaignID && isControl) {
      campaigns.forEach((campaign, campaignIndex) => {
        if (campaign.controlCampaignId !== campaignID) return;
        setValue(`campaigns.${campaignIndex}.controlCampaignId`, "", { shouldDirty: true });
      });
    }

    removeCampaign(index);
  };

  if (experimentType === ExperimentType.Custom) {
    return (
      <div>
        <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Campaigns</Label>

        {campaignFields.length > 0 && (
          <div className="grid grid-cols-[minmax(0,1fr)_120px_100px_40px] gap-2 mb-2">
            <div className="text-sm font-medium text-muted-foreground">Campaign</div>
            <div className="text-sm font-medium text-muted-foreground">Variant</div>
            <div className="text-sm font-medium text-muted-foreground">Traffic Share</div>
            <div></div>
          </div>
        )}

        <div className="space-y-2">
          {campaignFields.map((field, index) => {
            const campaign = campaigns[index];
            return (
              <div key={field.id} className="space-y-2">
                <div className="grid grid-cols-[minmax(0,1fr)_120px_100px_40px] gap-2 items-center">
                  <Combobox
                    value={campaign?.campaignId || ""}
                    setValue={value => handleCampaignIDChange(index, value)}
                    options={campaignOptions.map(c => ({
                      label: `${c.id} - ${c.name}`,
                      value: c.id.toString(),
                    }))}
                    buttonPlaceholder="Select campaign"
                    searchPlaceholder="Search campaigns"
                    notFoundMessage="No campaigns found"
                    isLoading={audienceOptionsLoading}
                    disabled={audiences.length === 0}
                  />
                  <Select
                    value={campaign?.variant || "test"}
                    onValueChange={value => handleCampaignVariantChange(index, value as ExperimentVariant)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="control">Control</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formatTrafficAllocationDisplay(campaign?.trafficAllocation || 0)}
                      onChange={e =>
                        setValue(`campaigns.${index}.trafficAllocation`, parseTrafficAllocationInput(e.target.value), {
                          shouldDirty: true,
                        })
                      }
                      className="text-right pr-7"
                      placeholder="0"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      %
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <Button type="button" variant="outline" size="icon" onClick={() => handleRemoveCampaign(index)}>
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="ml-4 pl-4 border-l-2 border-muted">
                  {(campaign?.params || []).map((param, paramIndex: number) => (
                    <div key={paramIndex} className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center mb-2">
                      <Input
                        value={param.paramName}
                        onChange={e => updateCampaignParam(index, paramIndex, "paramName", e.target.value)}
                        placeholder="Parameter name"
                      />
                      <Input
                        value={param.paramValue}
                        onChange={e => updateCampaignParam(index, paramIndex, "paramValue", e.target.value)}
                        placeholder="Parameter value"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeCampaignParam(index, paramIndex)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => addCampaignParam(index)}
                    className="text-xs text-muted-foreground"
                  >
                    + Add Parameter
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendCampaign({
                campaignId: "",
                controlCampaignId: "",
                testGroup: 1,
                variant: "test",
                trafficAllocation: 0,
                params: [],
              })
            }
            disabled={audiences.length === 0}
          >
            + Add Campaign
          </Button>

          <div
            className={`text-sm font-medium ${totalCampaignTrafficAllocation === 10000 ? "text-green-600" : "text-destructive"}`}
          >
            Total: {formatTrafficAllocationDisplay(totalCampaignTrafficAllocation)}%{" "}
            {totalCampaignTrafficAllocation === 10000 ? "✓" : "(must be 100%)"}
          </div>
        </div>

        {testCampaignIndexes.length > 0 && (
          <div className="mt-5">
            <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Test Groups</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Each column is a test group — the same treatment compared, in parallel, against every control. Pick the
              test campaign that runs against each control in each group; leave a cell empty if a control has no test in
              that group.
            </p>

            {controlRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">Add a control campaign above to assign test groups.</p>
            ) : (
              <div className="overflow-x-auto">
                {(() => {
                  const groupColumns = Array.from({ length: groupCount }, (_, i) => i + 1);
                  const lastGroupEmpty = !controlRows.some(({ campaign }) =>
                    placedTestByCell.has(`${campaign.campaignId}:${groupCount}`),
                  );
                  const gridTemplateColumns = `200px repeat(${groupCount}, minmax(180px, 1fr))`;
                  return (
                    <div className="min-w-fit space-y-1">
                      <div className="grid items-center gap-2 px-1 py-1" style={{ gridTemplateColumns }}>
                        <div className="text-sm font-medium text-muted-foreground">Control</div>
                        {groupColumns.map(group => (
                          <div
                            key={group}
                            className="flex items-center gap-1 text-sm font-medium text-muted-foreground"
                          >
                            Group {group}
                            {group === groupCount && groupCount > 1 && extraGroupColumns > 0 && lastGroupEmpty && (
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() => setExtraGroupColumns(n => Math.max(0, n - 1))}
                                aria-label={`Remove group ${group}`}
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {controlRows.map(({ campaign: control }) => (
                        <div
                          key={control.campaignId}
                          className="grid items-center gap-2 rounded-md bg-muted/20 px-1 py-2"
                          style={{ gridTemplateColumns }}
                        >
                          <div className="min-w-0 truncate px-2 text-sm" title={campaignLabel(control.campaignId)}>
                            {campaignLabel(control.campaignId)}
                          </div>
                          {groupColumns.map(group => {
                            const placed = placedTestByCell.get(`${control.campaignId}:${group}`);
                            const cellOptions = [
                              { label: "— None —", value: NONE_CELL_VALUE },
                              ...unassignedTests.map(({ campaign }) => ({
                                label: campaignLabel(campaign.campaignId),
                                value: campaign.campaignId,
                              })),
                              ...(placed
                                ? [{ label: campaignLabel(placed.campaignId), value: placed.campaignId }]
                                : []),
                            ];
                            return (
                              <Combobox
                                key={group}
                                value={placed?.campaignId ?? ""}
                                setValue={value =>
                                  handleCellChange(control.campaignId, group, value === NONE_CELL_VALUE ? "" : value)
                                }
                                options={cellOptions}
                                buttonPlaceholder="— None —"
                                searchPlaceholder="Search tests"
                                notFoundMessage="No unassigned test campaigns"
                                isLoading={false}
                                disabled={false}
                              />
                            );
                          })}
                        </div>
                      ))}

                      <div className="pt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs text-muted-foreground"
                          onClick={() => setExtraGroupColumns(n => n + 1)}
                        >
                          + Add test group
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {audiences.length === 0 && (
          <p className="text-sm text-muted-foreground mt-1">Select at least one audience first to add campaigns</p>
        )}
        {fieldErrors.campaigns && <p className="text-sm text-destructive mt-1">{fieldErrors.campaigns}</p>}

        <div className="mt-6">
          <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Vertex AI run monitoring (optional)</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Add the model (repo) and branch (version-id prefix) of each prediction pipeline tied to this experiment to
            surface its Vertex AI runs in diagnostics and alert on failures. Leave empty to skip monitoring.
          </p>

          {repoBranchPairFields.length > 0 && (
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 mb-2">
              <div className="text-sm font-medium text-muted-foreground">Repo (model-name)</div>
              <div className="text-sm font-medium text-muted-foreground">Branch (version-id prefix)</div>
              <div></div>
            </div>
          )}

          <div className="space-y-2">
            {repoBranchPairFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 items-center">
                <Combobox
                  value={repoBranchPairs[index]?.repo || ""}
                  setValue={value =>
                    setValue(`repoBranchPairs.${index}.repo`, value === NONE_CELL_VALUE ? "" : value, {
                      shouldDirty: true,
                    })
                  }
                  options={monitoredModels.map(model => ({ label: model, value: model }))}
                  buttonPlaceholder="Select repo"
                  searchPlaceholder="Search repos"
                  notFoundMessage="No monitored repos found"
                  isLoading={false}
                  disabled={monitoredModels.length === 0}
                />
                <Input placeholder="e.g. UA-1152_a4026" {...register(`repoBranchPairs.${index}.branch`)} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRepoBranchPair(index)}
                  aria-label="Remove repo/branch pair"
                >
                  <TrashIcon />
                </Button>
              </div>
            ))}
          </div>

          <div className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => appendRepoBranchPair({ repo: "", branch: "" })}
            >
              + Add repo/branch pair
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (experimentType === ExperimentType.Global) {
    return (
      <div>
        <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Prediction Flights</Label>

        {predictionFlightFields.length > 0 && (
          <div className="grid grid-cols-[1fr_120px_100px_40px] gap-2 mb-2">
            <div className="text-sm font-medium text-muted-foreground">Prediction Flight</div>
            <div className="text-sm font-medium text-muted-foreground">Variant</div>
            <div className="text-sm font-medium text-muted-foreground">Traffic Share</div>
            <div></div>
          </div>
        )}

        <div className="space-y-2">
          {predictionFlightFields.map((field, index) => {
            const predictionFlight = predictionFlights[index];
            const importedPredictionFlight = importMetadata?.prediction_flights?.find(
              importedFlight => importedFlight.prediction_flight_id.toString() === predictionFlight?.predictionFlightId,
            );
            const predictorSources =
              importedPredictionFlight?.predictor_configs?.filter(
                predictorConfig => predictorConfig.repo?.trim() || predictorConfig.branch?.trim(),
              ) || [];

            return (
              <div key={field.id} className="space-y-2">
                <div className="grid grid-cols-[1fr_120px_100px_40px] gap-2 items-center">
                  <Combobox
                    value={predictionFlight?.predictionFlightId || ""}
                    setValue={value =>
                      setValue(`predictionFlights.${index}.predictionFlightId`, value, { shouldDirty: true })
                    }
                    options={predictionFlightOptions}
                    buttonPlaceholder="Select prediction flight"
                    searchPlaceholder="Search prediction flights"
                    notFoundMessage="No prediction flights found"
                    isLoading={predictionFlightsLoading}
                    disabled={lockPredictionFlights}
                  />
                  <Select
                    value={predictionFlight?.variant || "test"}
                    onValueChange={value =>
                      setValue(`predictionFlights.${index}.variant`, value as ExperimentVariant, { shouldDirty: true })
                    }
                    disabled={lockPredictionFlights}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="control">Control</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formatTrafficAllocationDisplay(predictionFlight?.trafficAllocation || 0)}
                      onChange={e =>
                        setValue(
                          `predictionFlights.${index}.trafficAllocation`,
                          parseTrafficAllocationInput(e.target.value),
                          { shouldDirty: true },
                        )
                      }
                      className="text-right pr-7"
                      placeholder="0"
                      disabled={lockPredictionFlights}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      %
                    </span>
                  </div>
                  <div className="flex justify-end">
                    {!lockPredictionFlights && (
                      <Button type="button" variant="outline" size="icon" onClick={() => removePredictionFlight(index)}>
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="ml-4 pl-4 border-l-2 border-muted">
                  {(predictionFlight?.params || []).map((param, paramIndex: number) => (
                    <div key={paramIndex} className="grid grid-cols-[1fr_minmax(0,1fr)_40px] gap-2 items-start mb-2">
                      <Select
                        value={param.predictorTypeId !== null ? String(param.predictorTypeId) : ""}
                        onValueChange={value =>
                          updatePredictionFlightParamType(index, paramIndex, value !== "" ? parseInt(value, 10) : null)
                        }
                        disabled={lockPredictionFlights}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Predictor type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PREDICTOR_NAMES).map(([id, name]) => (
                            <SelectItem key={id} value={id}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <textarea
                        value={param.paramValue}
                        onChange={e => updatePredictionFlightParamValue(index, paramIndex, e.target.value)}
                        placeholder="Parameter value"
                        rows={getJsonTextareaRows(param.paramValue || "")}
                        spellCheck={false}
                        disabled={lockPredictionFlights}
                        className="flex min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      {!lockPredictionFlights && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removePredictionFlightParam(index, paramIndex)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {predictorSources.length > 0 && (
                    <div className="mb-2 grid gap-1 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      {predictorSources.map(predictorConfig => (
                        <div
                          key={`${predictionFlight?.predictionFlightId}-${predictorConfig.predictor_type}`}
                          className="break-words"
                        >
                          <span className="font-medium text-foreground">
                            {predictorTypeName(predictorConfig.predictor_type)}
                          </span>
                          {": "}
                          Repo: {predictorConfig.repo?.trim() || "not set"} · Branch:{" "}
                          {predictorConfig.branch?.trim() || "not set"}
                        </div>
                      ))}
                    </div>
                  )}

                  {!lockPredictionFlights && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => addPredictionFlightParam(index)}
                      className="text-xs text-muted-foreground"
                    >
                      + Add Parameter
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!lockPredictionFlights && (
          <div className="flex items-center justify-between mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                appendPredictionFlight({ predictionFlightId: "", variant: "test", trafficAllocation: 0, params: [] })
              }
            >
              + Add Prediction Flight
            </Button>
          </div>
        )}

        {fieldErrors.prediction_flights && (
          <p className="text-sm text-destructive mt-1">{fieldErrors.prediction_flights}</p>
        )}
      </div>
    );
  }

  return null;
}
